import { useEffect, useRef, useState } from 'react';
import { Filter, ChevronsRight } from 'lucide-react';
import type { TabId, TabState } from '../types';
import { SUB_REGIONS } from '../utils/regions';
import { getFYOptions, isAllSub, isAllQ, isAllFY, formatSub } from '../utils/filter';
import { formatIsoAsDdMmYy, hasDateRange } from '../utils/dateRange';
import { useRequests } from '../context/RequestsContext';
interface Props {
  tab: TabId;
  state: TabState & {
    status?: string;
  };
  onUpdate: (updates: Partial<TabState>) => void;
}
function getLabel(tab: TabId, s: TabState & {
  status?: string;
}): string {
  let lbl = s.r;
  const subLbl = formatSub(s.sub);
  if (subLbl) lbl += ' › ' + subLbl;
  if (tab === 'vp' && s.status) lbl += ' · ' + s.status;
  if (hasDateRange(s)) {
    const from = s.dateFrom ? formatIsoAsDdMmYy(s.dateFrom) : '';
    const to = s.dateTo ? formatIsoAsDdMmYy(s.dateTo) : '';
    lbl += ' · ' + (from && to ? `${from} – ${to}` : from ? `from ${from}` : `to ${to}`);
    return lbl;
  }
  const fyPart = isAllFY(s.fy) ? '' : s.fy.join(',');
  const qPart = isAllQ(s.q) ? '' : s.q.join(',');
  if (fyPart || qPart) lbl += ' · ' + (fyPart && qPart ? fyPart + '-' + qPart : fyPart || qPart);
  return lbl;
}
export function FilterBar({
  tab,
  state,
  onUpdate
}: Props) {
  const {
    requests
  } = useRequests();
  const fyOptions = getFYOptions(requests).filter(y => y !== 'All');
  const [fyOpen, setFyOpen] = useState(false);
  const fyRef = useRef<HTMLDivElement>(null);

  // Close the FY dropdown on outside click or Escape.
  useEffect(() => {
    if (!fyOpen) return;
    const onDown = (e: MouseEvent) => {
      if (fyRef.current && !fyRef.current.contains(e.target as Node)) setFyOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFyOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [fyOpen]);

  // Button text: "All FY", "FY2026", "FY2025, FY2026", or "FY2024 +2".
  const fySelected = (state.fy ?? []).filter(y => y !== 'All');
  const fyButtonLabel = isAllFY(state.fy) ? 'All FY' : fySelected.length <= 2 ? fySelected.map(y => 'FY' + y).join(', ') : `FY${fySelected[0]} +${fySelected.length - 1}`;
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const statuses = ['All', 'In Progress', 'Closed', 'On Hold', 'Cancelled'];
  const regions = ['Global', 'AMER', 'EMEA', 'JAPAC'];
  const subRegions = SUB_REGIONS[state.r] ?? [];
  function setRegion(r: string) {
    // On region change, reset sub-region selection to the whole region ("All").
    onUpdate({
      r,
      sub: ['All']
    });
  }

  // Toggle a single sub-region on/off. Selecting any sub clears the "All"
  // sentinel; deselecting the last selected sub snaps back to ['All'].
  function toggleSub(s: string) {
    const cur = (state.sub ?? []).filter(x => x !== 'All');
    const next = cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s];
    onUpdate({
      sub: next.length ? next : ['All']
    });
  }

  // Toggle a single fiscal year on/off. Same "All" sentinel behaviour as quarters.
  function toggleFY(y: string) {
    const cur = (state.fy ?? []).filter(x => x !== 'All');
    const next = cur.includes(y) ? cur.filter(x => x !== y) : [...cur, y];
    const ordered = fyOptions.filter(x => next.includes(x));
    onUpdate({
      fy: ordered.length ? ordered : ['All'],
      ...CLEAR_RANGE
    });
  }

  // Toggle a single quarter on/off. Selecting any quarter clears the "All"
  // sentinel; deselecting the last selected quarter snaps back to ['All'].
  function toggleQ(q: string) {
    const cur = (state.q ?? []).filter(x => x !== 'All');
    const next = cur.includes(q) ? cur.filter(x => x !== q) : [...cur, q];
    const ordered = quarters.filter(x => next.includes(x));
    onUpdate({
      q: ordered.length ? ordered : ['All'],
      ...CLEAR_RANGE
    });
  }

  // Created Quarter and Date Range are mutually exclusive: using one resets the other.
  const CLEAR_RANGE = {
    dateFrom: '',
    dateTo: ''
  };
  const rangeActive = hasDateRange(state);
  function setDateEnd(end: 'dateFrom' | 'dateTo', value: string) {
    const other = end === 'dateFrom' ? state.dateTo : state.dateFrom;
    const updates: Partial<TabState> = {
      [end]: value,
      fy: ['All'],
      q: ['All']
    };
    // Keep the range well-ordered: dragging one end past the other pulls the other along.
    if (value && other && (end === 'dateFrom' ? value > other : value < other)) {
      updates[end === 'dateFrom' ? 'dateTo' : 'dateFrom'] = value;
    }
    onUpdate(updates);
  }
  function clearRange() {
    onUpdate({
      ...CLEAR_RANGE,
      fy: ['All'],
      q: ['All']
    });
  }
  return <div className="bg-white px-[18px] py-[10px] flex gap-4 flex-wrap items-start border-b border-[#e8eaf0] shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-id="element-140" data-mp="3innpe2jqz0x4">
      {/* Region */}
      <div className="flex flex-col gap-[6px]" data-id="element-141" data-mp="1gtxxun12tuho">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-142" data-mp="1qy4v4dvuc6pt">Region</div>
        <div className="flex gap-1 flex-wrap" data-id="element-143" data-mp="1lzjvogoexr2f">
          {regions.map(r => <button key={r} className={`rpill ${state.r === r ? 'active' : ''}`} onClick={() => setRegion(r)} data-id="element-144" data-mp="1lxgv52pmwfjg">
              {r}
            </button>)}
        </div>
        {subRegions.length > 0 && <div className="flex items-center gap-[6px] pt-1 flex-wrap" data-id="element-145" data-mp="29oma7fnr33sp">
            <span className="text-[9px] font-semibold text-[#8892a0] uppercase tracking-[.05em] whitespace-nowrap flex items-center gap-1" data-id="element-146" data-mp="29qyzyezr2igb">
              <ChevronsRight className="w-[10px] h-[10px] text-[#d0d5dd]" data-id="element-147" data-mp="2iq2klnekf5wx" />Sub-region
            </span>
            <div className="flex gap-1 flex-wrap" data-id="element-148" data-mp="2enpg12bk1e1q">
              <button className={`srpill ${isAllSub(state.sub) ? 'active' : ''}`} onClick={() => onUpdate({
            sub: ['All']
          })} data-id="element-149" data-mp="2elusdg8wf7ty">
                All {state.r}
              </button>
              {subRegions.map(s => <button key={s} className={`srpill ${state.sub.includes(s) ? 'active' : ''}`} onClick={() => toggleSub(s)} data-id="element-150" data-mp="2jfzomn71ip19">
                  {s}
                </button>)}
            </div>
          </div>}
      </div>

      {/* Status (VP only) */}
      {tab === 'vp' && <div className="flex flex-col gap-[6px]" data-id="element-151" data-mp="32e3lai5qqvm6">
          <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-152" data-mp="32dabf8bhjpzt">Status</div>
          <div className="flex gap-1 flex-wrap" data-id="element-153" data-mp="37au257pddv8n">
            {statuses.map(s => <button key={s} className={`rpill ${(state.status ?? 'All') === s ? 'active' : ''}`} onClick={() => onUpdate({
          status: s
        })} data-id="element-154" data-mp="3c3vizl9504uv">
                {s}
              </button>)}
          </div>
        </div>}

      {/* Created Quarter */}
      <div className="flex flex-col gap-[6px]" data-id="element-155" data-mp="3q11viy39p7bn">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-156" data-mp="1eg1d0z5o9m2a">Created Quarter</div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-157" data-mp="1ad4v7anyhtze">
          <div ref={fyRef} className="relative" data-id="element-158" data-mp="15euw4oz4298i">
            <button type="button" className="fy-select" style={{
            textAlign: 'left',
            minWidth: 96,
            whiteSpace: 'nowrap'
          }} aria-haspopup="listbox" aria-expanded={fyOpen} aria-label="Created fiscal year" onClick={() => setFyOpen(o => !o)} data-id="element-159" data-mp="15fmsi20j8azu">
              {fyButtonLabel}
            </button>
            {fyOpen && <div role="listbox" aria-multiselectable="true" className="absolute left-0 top-[30px] z-30 min-w-[130px] bg-white border border-[#d0d5dd] rounded-[6px] shadow-[0_4px_12px_rgba(0,0,0,0.12)] py-1" data-id="element-160" data-mp="1obw5sn3m0xis">
                <label className="flex items-center gap-2 px-3 py-[5px] text-[11px] text-[#344054] cursor-pointer hover:bg-[#f5f7fa]" data-id="element-161" data-mp="2c1ytys4eljc4">
                  <input type="checkbox" checked={isAllFY(state.fy)} onChange={() => onUpdate({
                fy: ['All'],
                ...CLEAR_RANGE
              })} data-id="element-162" data-mp="1p6ovadc9xyae" />
                  All FY
                </label>
                <div className="h-px bg-[#e8eaf0] my-1" data-id="element-163" data-mp="1fhos0hqqogqe" />
                {fyOptions.map(y => <label key={y} className="flex items-center gap-2 px-3 py-[5px] text-[11px] text-[#344054] cursor-pointer hover:bg-[#f5f7fa]" data-id="element-164" data-mp="237rjavmtxiny">
                    <input type="checkbox" checked={state.fy.includes(y)} onChange={() => toggleFY(y)} data-id="element-165" data-mp="1u59wog39io0f" />
                    {'FY' + y}
                  </label>)}
              </div>}
          </div>
          <div className="w-px h-[18px] bg-[#e8eaf0] flex-shrink-0" data-id="element-166" data-mp="nr0wbl7u43k4" />
          <button className={`cqfpill ${isAllQ(state.q) ? 'active' : ''}`} onClick={() => onUpdate({
          q: ['All'],
          ...CLEAR_RANGE
        })} data-id="element-167" data-mp="f7nv0ff8h1pm">
            All Q
          </button>
          {quarters.map(q => <button key={q} className={`cqfpill ${state.q.includes(q) ? 'active' : ''}`} onClick={() => toggleQ(q)} data-id="element-168" data-mp="p4sfmatfkp4b">
              {q}
            </button>)}
        </div>
      </div>

      {/* Date Range (created date) -- mutually exclusive with Created Quarter */}
      <div className="flex flex-col gap-[6px]" data-id="element-169" data-mp="67r1o0hwfeo8">
        <div className="text-[9px] font-bold text-[#8892a0] uppercase tracking-[.06em]" data-id="element-170" data-mp="69lo5j8tjeuf">Date Range</div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-171" data-mp="1fzgy6lfc360">
          <input type="date" className="dr-input" aria-label="Created from" value={state.dateFrom ?? ''} max={state.dateTo || undefined} onChange={e => setDateEnd('dateFrom', e.target.value)} data-id="element-172" data-mp="39wc4h73vaimb" />
          <span className="text-[10px] text-[#8892a0]" data-id="element-173" data-mp="27bhh9epcbu0p">–</span>
          <input type="date" className="dr-input" aria-label="Created to" value={state.dateTo ?? ''} min={state.dateFrom || undefined} onChange={e => setDateEnd('dateTo', e.target.value)} data-id="element-174" data-mp="27bzjcryop2gj" />
          {rangeActive && <button className="dr-clear" onClick={clearRange} data-id="element-175" data-mp="1tcrmw6two8g1">Clear</button>}
        </div>
      </div>

      {/* Summary label */}
      <div className="text-[10px] text-[#8892a0] ml-auto self-center flex items-center gap-1" data-id="element-176" data-mp="1jl3y6doqh5i9">
        <Filter className="w-[11px] h-[11px]" data-id="element-177" data-mp="1emsoq42cox0k" />
        {getLabel(tab, state)}
      </div>
    </div>;
}